import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Sparkles, Repeat, Info, ArrowRight } from "lucide-react";
import { Seo } from "../components/Seo";
import { thumb } from "../lib/img";
import { getContextShop } from "../lib/shopContext";
import { PER_ME_MOODS, PER_ME_DESCRIPTION, PER_ME_QUESTION, PER_ME_NOTICE } from "../lib/perMe";
import { SubscriptionDialog } from "../components/SubscriptionDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MoodCard = ({ mood, active, onClick }) => (
  <button type="button" onClick={onClick} className={`group relative text-left rounded-2xl overflow-hidden aspect-[4/5] sm:aspect-[3/4] transition-transform duration-300 hover:-translate-y-1 ${active ? "ring-4 ring-[#B85C38]" : ""}`} data-testid={`mood-${mood.id}`}>
    <img src={mood.image} alt={mood.label} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
    <div className="absolute bottom-0 p-4 sm:p-5 text-white">
      <p className="font-serif text-lg sm:text-xl leading-tight">{mood.label}</p>
      <p className="text-xs text-white/80 mt-1">{mood.hint}</p>
    </div>
  </button>
);

export default function PerMePage() {
  const [params, setParams] = useSearchParams();
  const mood = params.get("voglia") || "";
  const [products, setProducts] = useState([]);
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(false);
  const [subFor, setSubFor] = useState(null);
  const ctx = getContextShop();

  useEffect(() => {
    setProducts([]);
    if (!mood) return;
    setLoading(true);
    axios.get(`${API}/per-me/products`, { params: { mood, shop_id: ctx?.id || undefined } })
      .then(r => { setProducts(r.data.products); setShop(r.data.shop); })
      .catch(() => setProducts([])).finally(() => setLoading(false));
    setTimeout(() => document.getElementById("per-me-results")?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mood]);

  const pick = (id) => setParams(id === mood ? {} : { voglia: id });
  const title = mood === "momento" ? "Fiori del momento" : PER_ME_MOODS.find(m => m.id === mood)?.label;

  return (
    <div className="bg-[#FBF7F1] min-h-screen" data-testid="per-me-page">
      <Seo title="Fiori per me · Flower Live" description={PER_ME_DESCRIPTION} path="/fiori-per-me" image="/per-me/hero.jpg" />
      <section className="relative overflow-hidden">
        <img src="/per-me/hero.jpg" alt="Fiori freschi in casa" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#2b1d14]/80 via-[#2b1d14]/50 to-transparent" />
        <div className="relative max-w-6xl mx-auto px-6 py-24 sm:py-32 text-white">
          <p className="text-xs tracking-[0.3em] uppercase text-[#F3C9A8] mb-4">Fiori per me</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-2xl" data-testid="per-me-question">{PER_ME_QUESTION}</h1>
          <p className="text-base md:text-lg text-white/85 max-w-xl mt-6 leading-relaxed" data-testid="per-me-description">{PER_ME_DESCRIPTION}</p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 -mt-10 relative">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {PER_ME_MOODS.map(m => <MoodCard key={m.id} mood={m} active={mood === m.id} onClick={() => pick(m.id)} />)}
        </div>
        <button type="button" onClick={() => pick("momento")} className={`mt-4 w-full rounded-2xl p-5 sm:p-6 flex items-center gap-4 text-left transition-colors ${mood === "momento" ? "bg-[#B85C38] text-white" : "bg-[#2D5016] text-white hover:bg-[#3a6a1c]"}`} data-testid="mood-momento">
          <span className="w-12 h-12 rounded-full bg-white/15 flex items-center justify-center shrink-0"><Sparkles className="h-6 w-6" /></span>
          <span className="flex-1">
            <span className="block font-serif text-xl sm:text-2xl">Fiori del momento</span>
            <span className="block text-sm text-white/80">Quello che il fiorista ha di più fresco questa settimana: stagionali, appena arrivati, da prendere al volo</span>
          </span>
          <ArrowRight className="h-5 w-5 shrink-0" />
        </button>
      </section>

      <p className="max-w-6xl mx-auto px-6 mt-5 text-xs text-sage flex items-start gap-1.5" data-testid="per-me-notice-top"><Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />Mazzi misti o mono fiore incartati con carta: senza confezione regalo, senza bigliettino, pronti per il tuo vaso.</p>

      <section id="per-me-results" className="max-w-6xl mx-auto px-6 py-14 scroll-mt-24">
        {!mood ? (
          <p className="text-center text-sage" data-testid="per-me-hint">Scegli una voglia qui sopra: ti mostriamo solo i fiori adatti, pronti per il tuo vaso.</p>
        ) : (
          <>
            <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
              <div>
                <h2 className="font-serif text-2xl sm:text-3xl text-forest" data-testid="per-me-results-title">{title}</h2>
                {shop?.name && <p className="text-sm text-sage mt-1">Preparati da <Link to={shop.subdomain ? `/fioristi/${shop.subdomain}` : "/shops"} className="underline underline-offset-2">{shop.name}</Link>{ctx?.city ? ` · consegna a ${ctx.city}` : ""}</p>}
              </div>
              <p className="text-xs text-sage">{products.length} proposte</p>
            </div>
            <div className="flex items-start gap-2 text-xs text-[#8a5a3a] bg-[#FDF0E6] border border-[#F3C9A8] rounded-xl px-4 py-3 mb-8" data-testid="per-me-notice"><Info className="h-4 w-4 shrink-0 mt-0.5" />{PER_ME_NOTICE}</div>
            {loading ? <p className="text-sage">Caricamento…</p> : products.length === 0 ? (
              <div className="text-center py-14 border border-dashed border-cream-dark rounded-2xl" data-testid="per-me-empty">
                <p className="font-serif text-xl text-forest">Nessun fiore per questa voglia, per ora</p>
                <p className="text-sm text-sage mt-1">Prova un'altra categoria oppure <Link to="/fiori-per-me?voglia=momento" className="underline">guarda i Fiori del momento</Link>.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
                {products.map(p => (
                  <div key={p.id} className="group bg-white rounded-2xl overflow-hidden border border-cream-dark flex flex-col" data-testid={`per-me-product-${p.id}`}>
                    <Link to={`/products/${p.id}`} className="block aspect-[4/5] overflow-hidden bg-cream relative">
                      <img src={thumb(p.images?.[0], 600) || "/product-placeholder.svg"} alt={p.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                      {p.of_the_moment && <span className="absolute top-2 left-2 bg-[#B85C38] text-white text-[10px] px-2 py-1 rounded-full inline-flex items-center gap-1"><Sparkles className="h-3 w-3" />Del momento</span>}
                    </Link>
                    <div className="p-3 sm:p-4 flex flex-col flex-1">
                      <Link to={`/products/${p.id}`} className="font-serif text-forest leading-tight line-clamp-2">{p.name}</Link>
                      <p className="text-sm text-sage mt-1">da €{(p.display_price ?? p.price).toFixed(2)}</p>
                      <div className="mt-auto pt-3 flex gap-2">
                        <Link to={`/products/${p.id}`} className="flex-1 text-center text-xs sm:text-sm bg-forest text-white rounded-full py-2 hover:bg-forest-light" data-testid={`per-me-buy-${p.id}`}>Lo voglio</Link>
                        <button type="button" onClick={() => setSubFor(p)} title="Ricevilo ogni settimana" className="px-3 rounded-full border border-[#B85C38] text-[#B85C38] hover:bg-[#B85C38] hover:text-white transition-colors" data-testid={`per-me-sub-${p.id}`}><Repeat className="h-4 w-4" /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-20">
        <div className="rounded-3xl bg-[#2D5016] text-white p-8 sm:p-12 grid md:grid-cols-[1fr_auto] gap-6 items-center" data-testid="per-me-subscription-banner">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-[#F3C9A8] mb-3">Abbonamento</p>
            <h2 className="font-serif text-2xl sm:text-3xl leading-tight">Fiori freschi a casa, ogni settimana</h2>
            <p className="text-white/80 mt-3 max-w-xl leading-relaxed">Scegli il mazzo che ami, lo ricevi ogni 7 o 14 giorni nel giorno che preferisci. Nessun vincolo: sospendi quando vuoi con un clic.</p>
          </div>
          <Link to="/fiori-per-me?voglia=casa" className="inline-flex items-center gap-2 bg-[#B85C38] hover:bg-[#9c4a2c] text-white px-6 py-3 rounded-full" data-testid="per-me-sub-cta"><Repeat className="h-4 w-4" />Scegli il tuo mazzo</Link>
        </div>
      </section>
      <SubscriptionDialog product={subFor} open={Boolean(subFor)} onOpenChange={(o) => !o && setSubFor(null)} />
    </div>
  );
}
